import client from "@/client/publicApiClient.ts";
import { useQuery, queryOptions, useSuspenseQuery } from "@tanstack/react-query";
import type { GetAllClientIdentityApisQueryResponse, GetAllClientIdentityApisPathParams, GetAllClientIdentityApisQueryParams } from "../types/GetAllClientIdentityApis";
import type { QueryObserverOptions, UseQueryResult, QueryKey, UseSuspenseQueryOptions, UseSuspenseQueryResult } from "@tanstack/react-query";

 type GetAllClientIdentityApisClient = typeof client<GetAllClientIdentityApisQueryResponse, never, never>;
type GetAllClientIdentityApis = {
    data: GetAllClientIdentityApisQueryResponse;
    error: never;
    request: never;
    pathParams: GetAllClientIdentityApisPathParams;
    queryParams: GetAllClientIdentityApisQueryParams;
    headerParams: never;
    response: GetAllClientIdentityApisQueryResponse;
    client: {
        parameters: Partial<Parameters<GetAllClientIdentityApisClient>[0]>;
        return: Awaited<ReturnType<GetAllClientIdentityApisClient>>;
    };
};
export const getAllClientIdentityApisQueryKey = (clientCode: GetAllClientIdentityApisPathParams["clientCode"], params?: GetAllClientIdentityApis["queryParams"]) => [{ url: "/api/clients/:clientCode/identities", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientIdentityApisQueryKey = ReturnType<typeof getAllClientIdentityApisQueryKey>;
export function getAllClientIdentityApisQueryOptions(clientCode: GetAllClientIdentityApisPathParams["clientCode"], params?: GetAllClientIdentityApis["queryParams"], options: GetAllClientIdentityApis["client"]["parameters"] = {}) {
    const queryKey = getAllClientIdentityApisQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientIdentityApis["data"], GetAllClientIdentityApis["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/identities`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all API Key identities for a given client. The API Key or user that
is calling this endpoint must have access to the client with the given
client code.
 * @summary Get all API Keys
 * @link /api/clients/:clientCode/identities
 */
export function useGetAllClientIdentityApis<TData = GetAllClientIdentityApis["response"], TQueryData = GetAllClientIdentityApis["response"], TQueryKey extends QueryKey = GetAllClientIdentityApisQueryKey>(clientCode: GetAllClientIdentityApisPathParams["clientCode"], params?: GetAllClientIdentityApis["queryParams"], options: {
    query?: Partial<QueryObserverOptions<GetAllClientIdentityApis["response"], GetAllClientIdentityApis["error"], TData, TQueryData, TQueryKey>>;
    client?: GetAllClientIdentityApis["client"]["parameters"];
} = {}): UseQueryResult<TData, GetAllClientIdentityApis["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientIdentityApisQueryKey(clientCode, params);
    const query = useQuery({
        ...getAllClientIdentityApisQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseQueryResult<TData, GetAllClientIdentityApis["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}
export const getAllClientIdentityApisSuspenseQueryKey = (clientCode: GetAllClientIdentityApisPathParams["clientCode"], params?: GetAllClientIdentityApis["queryParams"]) => [{ url: "/api/clients/:clientCode/identities", params: { clientCode: clientCode } }, ...(params ? [params] : [])] as const;
export type GetAllClientIdentityApisSuspenseQueryKey = ReturnType<typeof getAllClientIdentityApisSuspenseQueryKey>;
export function getAllClientIdentityApisSuspenseQueryOptions(clientCode: GetAllClientIdentityApisPathParams["clientCode"], params?: GetAllClientIdentityApis["queryParams"], options: GetAllClientIdentityApis["client"]["parameters"] = {}) {
    const queryKey = getAllClientIdentityApisSuspenseQueryKey(clientCode, params);
    return queryOptions({
        queryKey,
        queryFn: async () => {
            const res = await client<GetAllClientIdentityApis["data"], GetAllClientIdentityApis["error"]>({
                method: "get",
                url: `/api/clients/${clientCode}/identities`,
                params,
                ...options
            });
            return res.data;
        },
    });
}
/**
 * @description Gets all API Key identities for a given client. The API Key or user that
is calling this endpoint must have access to the client with the given
client code.
 * @summary Get all API Keys
 * @link /api/clients/:clientCode/identities
 */
export function useGetAllClientIdentityApisSuspense<TData = GetAllClientIdentityApis["response"], TQueryKey extends QueryKey = GetAllClientIdentityApisSuspenseQueryKey>(clientCode: GetAllClientIdentityApisPathParams["clientCode"], params?: GetAllClientIdentityApis["queryParams"], options: {
    query?: Partial<UseSuspenseQueryOptions<GetAllClientIdentityApis["response"], GetAllClientIdentityApis["error"], TData, TQueryKey>>;
    client?: GetAllClientIdentityApis["client"]["parameters"];
} = {}): UseSuspenseQueryResult<TData, GetAllClientIdentityApis["error"]> & {
    queryKey: TQueryKey;
} {
    const { query: queryOptions, client: clientOptions = {} } = options ?? {};
    const queryKey = queryOptions?.queryKey ?? getAllClientIdentityApisSuspenseQueryKey(clientCode, params);
    const query = useSuspenseQuery({
        ...getAllClientIdentityApisSuspenseQueryOptions(clientCode, params, clientOptions) as unknown as QueryObserverOptions,
        queryKey,
        ...queryOptions as unknown as Omit<QueryObserverOptions, "queryKey">
    }) as UseSuspenseQueryResult<TData, GetAllClientIdentityApis["error"]> & {
        queryKey: TQueryKey;
    };
    query.queryKey = queryKey as TQueryKey;
    return query;
}